import { api } from '../lib/api'

export type CreatorEmailAudienceSummary = {
  total: number
  subscribed: number
  broadcast_recipient_count?: number
  unsubscribed: number
  bounced: number
  complained: number
  last_import_at?: string | null
  sending_blocked?: boolean
  sending_block_reason?: string
}

export type CreatorEmailImportResult = {
  batch_id: string
  filename: string
  total_rows: number
  imported_count: number
  duplicate_count: number
  invalid_count: number
  first_invalid: Array<{ row_number: number; raw_email: string; error: string }>
  first_duplicates: Array<{ row_number: number; email: string }>
}

export type CreatorEmailCampaign = {
  id: string
  type: 'BROADCAST' | 'POST'
  subject: string
  body_text: string
  post_id?: number | null
  status: 'DRAFT' | 'QUEUED' | 'SENDING' | 'SENT' | 'FAILED'
  audience_filter: 'ALL_CONTACTS' | 'PLATFORM_OPTED_IN' | 'IMPORT_ONLY' | 'SUBSCRIBERS_ONLY'
  attempted: number
  delivered: number
  failed: number
  bounced: number
  complained: number
  unsubscribed: number
  last_error?: string
  created_at?: string
  sent_at?: string | null
}

export type CreatorEmailPreference = {
  user_id: number
  creator_id: number
  receive_posts: boolean
  receive_broadcasts: boolean
  updated_at?: string
}

export async function getCreatorEmailAudienceSummary() {
  const { data } = await api.get<CreatorEmailAudienceSummary>('/api/creator/email/audience/summary/')
  return data
}

export async function importCreatorEmailAudience(file: File, consentConfirmed: boolean) {
  const form = new FormData()
  form.append('file', file)
  form.append('consent_confirmed', consentConfirmed ? 'true' : 'false')
  const { data } = await api.post<CreatorEmailImportResult>('/api/creator/email/audience/import/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function addCreatorEmailContact(email: string) {
  const { data } = await api.post('/api/creator/email/audience/add/', { email })
  return data
}

export async function exportCreatorEmailAudienceCsv() {
  const { data } = await api.get('/api/creator/email/audience/export/', {
    responseType: 'blob',
  })
  return data as Blob
}

export async function queueCreatorBroadcast(payload: {
  subject: string
  body_text?: string
  audience_filter?: 'ALL_CONTACTS' | 'PLATFORM_OPTED_IN' | 'IMPORT_ONLY' | 'SUBSCRIBERS_ONLY'
}) {
  const { data } = await api.post<CreatorEmailCampaign & { recipient_count?: number }>('/api/creator/email/campaigns/broadcast/', payload)
  return data
}

export async function listCreatorEmailCampaigns() {
  const { data } = await api.get<{ results: CreatorEmailCampaign[] }>('/api/creator/email/campaigns/')
  return data
}

export async function getCreatorEmailPreference(creatorId: number) {
  const { data } = await api.get<CreatorEmailPreference>('/api/email/preferences/', {
    params: { creator_id: creatorId },
  })
  return data
}

export async function patchCreatorEmailPreference(payload: {
  creator_id: number
  receive_posts?: boolean
  receive_broadcasts?: boolean
}) {
  const { data } = await api.patch<CreatorEmailPreference>('/api/email/preferences/', payload)
  return data
}
